import { KeyboardEvent, PointerEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from './cn';
import { DOCK_MIN, DOCK_WIDTH, applyDockWidth, clampDockWidth } from '@/components/item-panel';

// Entrance is slower than exit so the panel arrives with weight and leaves briskly — the
// same choreography language as the Modal, but horizontal. Ease-out on the way in (settles
// into place), ease-in on the way out (accelerates away).
const OPEN_MS = 380;
const CLOSE_MS = 300;
const ENTER_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EXIT_EASE = 'cubic-bezier(0.4, 0, 0.9, 0.55)';

/** Mirrors the Modal's phase machine so a mid-exit reopen reverses smoothly instead of
 *  snapping off-screen: `preEnter` parks the panel off the right edge for one frame. */
type Phase = 'closed' | 'preEnter' | 'open' | 'closing';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  /** Control(s) placed in the header, just left of the close button (e.g. a pin toggle). */
  headerAccessory?: ReactNode;
  /** Show the header's close (×) button. Off for the empty state, where there's nothing to close
   *  (the pin toggle stays, so the sidebar can still be dismissed by unpinning). Default true. */
  showClose?: boolean;
  children: ReactNode;
  ariaLabel?: string;
  /** The member's committed width (px) — announced by the resize handle. */
  width?: number;
  /** Makes the left edge a drag handle; called with the final width on release (and on keyboard
   *  steps / double-click reset). While dragging, the width is written straight to `--dock-w` so
   *  the panel and the board inset track the pointer without a React re-render. */
  onResize?: (width: number) => void;
};

// One keyboard step of the resize handle (←/→).
const KEY_STEP = 32;

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * A right-side docked panel: a persistent, NON-modal complement to the board (no backdrop,
 * no scroll lock, no focus trap — the board stays fully interactive beside it). The app
 * shell insets its content by the same width (`--dock-w-eff`) so the two sit side by side. Slides in from
 * the right on open and out on close. Portals to <body> so it's viewport-fixed regardless of
 * any transformed/overflow ancestor in the view it's rendered from. With `onResize`, its left edge
 * is a drag handle (also ←/→ when focused, double-click / Home to reset).
 */
export const DrawerDock = ({ open, onClose, title, footer, headerAccessory, showClose = true, children, ariaLabel, width, onResize }: Props) => {
  const [phase, setPhase] = useState<Phase>(open ? 'open' : 'closed');
  const mounted = phase !== 'closed';
  const asideRef = useRef<HTMLElement>(null);
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    if (open) setPhase((prev) => (prev === 'closed' ? 'preEnter' : 'open'));
    else setPhase((prev) => (prev === 'closed' ? 'closed' : 'closing'));
  }, [open]);

  // Flip the one off-screen `preEnter` frame into `open` so the slide-in transition runs
  // (two rAFs: paint the off-screen position, then transition to rest).
  useEffect(() => {
    if (phase !== 'preEnter') return undefined;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setPhase('open')));
    return () => cancelAnimationFrame(r);
  }, [phase]);

  // Keep the panel mounted through the exit slide, then unmount.
  useEffect(() => {
    if (phase !== 'closing') return undefined;
    const t = setTimeout(() => setPhase('closed'), CLOSE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Width actually on screen (the stored preference may be clamped narrower by the viewport).
  const renderedWidth = () => asideRef.current?.getBoundingClientRect().width ?? width ?? DOCK_WIDTH;

  // Drag the left edge. Delta-based (not `innerWidth - clientX`) so grabbing a few px off the exact
  // edge doesn't make the panel jump. Pointer capture keeps the drag alive over iframes/editors.
  const onHandlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!onResize || e.button !== 0) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startW = renderedWidth();
    let last = startW;
    let moved = false;
    document.documentElement.classList.add('dock-resizing');
    setResizing(true);
    const move = (ev: globalThis.PointerEvent) => {
      moved = true;
      last = clampDockWidth(startW + (startX - ev.clientX));
      applyDockWidth(last);
    };
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      document.documentElement.classList.remove('dock-resizing');
      setResizing(false);
      // A plain click (no movement) commits nothing — it mustn't overwrite a wider stored width
      // with the viewport-clamped one on a narrow window.
      if (moved) onResize(last);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  const onHandleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onResize) return;
    if (e.key === 'ArrowLeft') onResize(renderedWidth() + KEY_STEP);
    else if (e.key === 'ArrowRight') onResize(renderedWidth() - KEY_STEP);
    else if (e.key === 'Home') onResize(DOCK_WIDTH);
    else return;
    e.preventDefault();
  };

  if (!mounted) return null;

  const closing = phase === 'closing';
  const atRest = phase === 'open';
  // The panel only ever translates (never fades — a solid surface should slide, so it reads
  // as leaving rather than dissolving). `translateX(0)` at rest (not `none`) keeps a
  // containing block for any `position: fixed` child (e.g. the drawer's drop veil).
  const translateX = atRest ? '0' : '100%';
  const transition =
    phase === 'preEnter' ? 'none' : `transform ${closing ? CLOSE_MS : OPEN_MS}ms ${closing ? EXIT_EASE : ENTER_EASE}`;

  return createPortal(
    <aside
      ref={asideRef}
      role="region"
      aria-label={ariaLabel ?? 'Details'}
      // z-[27] sits ABOVE board content (incl. the views' sticky column headers at z-[25], so a
      // page-scrolled table can't poke out over the panel) but BELOW the sticky app header
      // (z-30) and its dropdowns/flyouts — the header's stacking context lifts its whole subtree
      // over the panel, so the board/view switcher menus are never covered. Portaled popovers,
      // pickers and modals (z-[1000]+) still float above the panel as intended.
      className="dock-panel fixed right-0 z-[27] flex flex-col border-l border-line bg-surface shadow-2xl shadow-black/40"
      style={{
        top: '3.5rem', // sits below the sticky app header (h-14)
        bottom: 0,
        width: 'var(--dock-w-eff)',
        maxWidth: '100vw',
        transform: `translateX(${translateX})`,
        transition,
        willChange: 'transform',
      }}
    >
      {onResize ? (
        // The resize grip straddles the left border (half outside), so it's easy to hit without
        // stealing clicks from the panel's own content. A hairline lights up on hover / focus / drag.
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize details panel"
          aria-valuenow={width}
          aria-valuemin={DOCK_MIN}
          tabIndex={0}
          title="Drag to resize · double-click to reset"
          onPointerDown={onHandlePointerDown}
          onDoubleClick={() => onResize(DOCK_WIDTH)}
          onKeyDown={onHandleKeyDown}
          className="group absolute inset-y-0 -left-1.5 z-10 w-3 cursor-col-resize touch-none focus-visible:outline-none"
        >
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 transition-colors',
              'group-hover:bg-accent/60 group-focus-visible:bg-accent',
              resizing ? 'bg-accent' : 'bg-transparent',
            )}
          />
        </div>
      ) : null}
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0 flex-1">{title}</div>
        <div className="flex shrink-0 items-center gap-1">
          {headerAccessory}
          {showClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
            >
              <CloseIcon />
            </button>
          ) : null}
        </div>
      </div>
      <div className="thin-scroll flex-1 overflow-auto px-5 py-4">{children}</div>
      {footer ? <div className="border-t border-line px-5 py-3">{footer}</div> : null}
    </aside>,
    document.body,
  );
};

export default DrawerDock;
